import { getCurrentApi, authServiceRequest } from './baseAPI';

// Handle API errors consistently with the new API response formats
const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const errorData = error.response.data;
    
    // Check for nested error structure (errorData.data.error)
    const errorMessage = errorData?.data?.error ||
                         errorData?.details ||
                         errorData?.error ||
                         errorData?.message;
    
    switch (status) {
      case 400:
        return new Error(errorMessage || 'Bad Request');
      case 404:
        return new Error(errorMessage || 'User not found');
      case 500:
        return new Error(errorMessage || 'Server Error');
      default:
        return new Error(errorMessage || `Error ${status}`);
    }
  } else if (error.request) {
    // Network error
    return new Error('Network Error: Unable to connect to the server');
  } else {
    // Other error
    return new Error(error.message || 'An unexpected error occurred');
  }
};

export const userAPI = {
  // Get all users with filtering and pagination
  getAllUsers: async (params = {}) => {
    try {
      const api = await getCurrentApi();
      const response = await api.get('/users', { params });
      return response;
    } catch (error) {
      console.error(`GET /users error:`, error);
      throw handleApiError(error);
    }
  },

  // Get a specific user
  getUser: async (userId) => {
    try {
      const api = await getCurrentApi();
      const response = await api.get(`/users/${userId}`);
      return response;
    } catch (error) {
      console.error(`GET /users/${userId} error:`, error);
      throw handleApiError(error);
    }
  },

  // Get users not in a specific group
  getUsersNotInGroup: async (groupId, params = {}) => {
    try {
      const api = await getCurrentApi();
      const response = await api.get(`/users/not-in-group/${groupId}`, { params });
      return response;
    } catch (error) {
      console.error(`GET /users/not-in-group/${groupId} error:`, error);
      throw handleApiError(error);
    }
  },

  // Quick search for users
  searchUsers: async (identifier) => {
    try {
      const api = await getCurrentApi();
      const response = await api.get(`/users/search/${identifier}`);
      return response;
    } catch (error) {
      console.error(`GET /users/search/${identifier} error:`, error);
      throw handleApiError(error);
    }
  },

  // Update user information
  updateUser: async (userId, userData) => {
    try {
      const api = await getCurrentApi();
      const response = await api.put(`/users/${userId}`, userData);
      return response;
    } catch (error) {
      console.error(`PUT /users/${userId} error:`, error);
      throw handleApiError(error);
    }
  },

  // Create a new user
  createUser: async (userData) => {
    try {
      const api = await getCurrentApi();
      const response = await api.post('/users', userData);
      return response;
    } catch (error) {
      console.error('POST /users error:', error);
      throw handleApiError(error);
    }
  },

  // Legacy methods for backward compatibility
  getAll: async () => {
    console.warn('getAll() is deprecated, use getAllUsers() instead');
    return userAPI.getAllUsers();
  },
  
  blockUser: async (userId) => {
    if (!userId) {
      throw new Error('User ID is required to block a user');
    }
    
    try {
      const api = await getCurrentApi();
      const response = await api.put(`/users/${userId}/block`);
      return response;
    } catch (error) {
      console.error(`PUT /users/${userId}/block error:`, error);
      throw handleApiError(error);
    }
  },
  
  unblockUser: async (userId) => {
    if (!userId) {
      throw new Error('User ID is required to unblock a user');
    }
    
    try {
      const api = await getCurrentApi();
      const response = await api.put(`/users/${userId}/unblock`);
      return response;
    } catch (error) {
      console.error(`PUT /users/${userId}/unblock error:`, error);
      throw handleApiError(error);
    }
  },
  
  deleteUser: async (userId, reason = 'Admin action') => {
    if (!userId) {
      throw new Error('User ID is required to delete a user');
    }
    
    try {
      const api = await getCurrentApi();
      const response = await api.delete(`/users/${userId}`, { data: { reason } });
      return response;
    } catch (error) {
      console.error(`DELETE /users/${userId} error:`, error);
      throw handleApiError(error);
    }
  },
  
  resetRateLimits: async (userId) => {
    try {
      const api = await getCurrentApi();
      const response = await api.post(`/users/${userId}/reset-rate-limits`);
      return response;
    } catch (error) {
      console.error(`POST /users/${userId}/reset-rate-limits error:`, error);
      throw handleApiError(error);
    }
  }
};
